import React, { useEffect, useMemo, useState } from "react";
import { Image } from "@/components/common/image";
import { RefreshIcon, TrashIcon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";
import { audioControllerHooks } from "@/manager";
import { AudioPlayState } from "@/manager/manager/audio-controller-manager";
import { PlayIcon } from '@/components/agent-creation/assets/play-icon';
import { StopIcon } from '@/components/agent-creation/assets/stop-icon';
import { AudioControllerBar } from "@/components/audio-controller-bar";
import momentsBg2 from '@/assets/agent/moments-bg2.webp'
import { IconButton } from "../common/icon-button";
import { useTranslation } from "react-i18next";
import { ConfirmModal } from "../common/confirm-modal";
import { RadioButton } from "../common/radio-button";
import { AiMakeMessageRecordData } from "@/services/ai-make-source-record-list-by-type";
import { Video } from "../common/video";
import { FallbackImage } from "../common/fallback-image";
import { buildImageOssResize } from "@/utils/build-image-oss-resize ";
import { formatAudioTime } from "@/utils/format-audio-time";
import { buildVideoOssFrame } from "@/utils/build-video-oss-frame";

export type ShortVideoRecordItemProps = {
  thumbnail: string;
  content: string;
  duration?: number;
  className?: string;
  deleteLoading?: boolean;
  onDelete?: () => void;
  onTap?: () => void;
};

const fmt = (seconds: number) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, "0");
  const s = Math.floor(seconds % 60).toString().padStart(2, "0");
  return h > 0 ? `${h}:${m}:${s}` : `${m}:${s}`;
};

export const ShortVideoRecordItem: React.FC<ShortVideoRecordItemProps> = ({
  thumbnail,
  content,
  duration,
  deleteLoading = false,
  onDelete,
  onTap,
  className = "",
}) => {
  const { t } = useTranslation();
  return (
    <div
      onClick={onTap}
      className={className}
      role="button"
      tabIndex={0}
    >
      <div className="flex items-center group">
        <div className="relative w-[75px] h-[100px] rounded-[8px] overflow-hidden shrink-0">
          <Image
            src={thumbnail}
            alt="thumbnail"
            className="w-full h-full object-cover"
          />
          {typeof duration === "number" && (
            <div className="absolute left-1 bottom-1 px-[6px] py-[2px] rounded-[6px] bg-black/50 text-[10px] font-medium text-white">
              {fmt(duration)}
            </div>
          )}
        </div>
        <div className="ml-3 flex-1 flex items-center">
          <p className="text-[14px] text-[#333333] line-clamp-2  pr-1">
            {content}
          </p>
          <ConfirmModal
            trigger={
              <IconButton
                icon={<TrashIcon />}
                className="opacity-0 group-hover:opacity-100 transition-all"
              />
            }
            title={t('agent_creation.content.delete_title')}
            description={t('agent_creation.content.delete_desc')}
            cancel={t('common.cancel')}
            confirm={t('common.delete')}
            onConfirm={onDelete}
            loading={deleteLoading}
          />
        </div>
      </div>
    </div>
  );
};

export type ShortVideoGeneratingItemProps = {
  createdAt: number | Date;
  content: string;
  className?: string;
};

const progressPercent = (start: number | Date) => {
  const startMs = start instanceof Date ? start.getTime() : Number(start);
  const elapsed = Math.max(0, Math.floor((Date.now() - startMs) / 1000));
  const startSec = 60;
  const endSec = 240;
  if (elapsed <= startSec) return 0;
  if (elapsed >= endSec) return 99;
  const ratio = (elapsed - startSec) / (endSec - startSec);
  return Math.floor(ratio * 99);
};

export const ShortVideoGeneratingItem: React.FC<ShortVideoGeneratingItemProps> = ({
  createdAt,
  content,
  className = "",
}) => {
  const { t } = useTranslation();
  const pct = progressPercent(createdAt);
  return (
    <div className={className}>
      <div className="flex items-center">
        <div className="relative w-[75px] h-[100px] rounded-[8px] overflow-hidden shrink-0">
          <div className="w-full h-full bg-[linear-gradient(135deg,_rgba(189,168,255,1)_0%,_rgba(188,226,255,1)_100%)]" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex flex-col items-center">
              <span className="text-white text-[14px] font-medium">{pct}%</span>
              <span className="text-white text-[10px] font-medium mt-1">
                {t('agent_creation.content.regenerating')}
              </span>
            </div>
          </div>
        </div>
        <div className="ml-3 flex-1">
          <p className="text-[14px] text-[#333333] line-clamp-2">
            {content}
          </p>
        </div>
      </div>
    </div>
  );
};

export type ShortVideoFailedItemProps = {
  content: string;
  deleteLoading?: boolean;
  className?: string;
  onRegenerate?: () => void;
  onDelete?: () => void;
};

export const ShortVideoFailedItem: React.FC<ShortVideoFailedItemProps> = ({
  content,
  onRegenerate,
  onDelete,
  deleteLoading = false,
  className = "",
}) => {
  const { t } = useTranslation();
  return (
    <div className={className}>
      <div className="flex items-center">
        <button
          type="button"
          onClick={onRegenerate}
          className="relative w-[75px] h-[100px] rounded-[8px] overflow-hidden shrink-0"
        >
          <div className="w-full h-full bg-[linear-gradient(135deg,_rgba(189,168,255,1)_0%,_rgba(188,226,255,1)_100%)]" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex flex-col items-center">
              <RefreshIcon className="w-4 h-4 text-white" />
              <span className="text-white text-[10px] font-medium mt-1">
                {t('agent_creation.content.regenerate')}
              </span>
            </div>
          </div>
        </button>
        <div className="h-[100px] group ml-3 flex-1 flex items-center justify-between">
          <p className="text-[14px] text-[#333333] line-clamp-2 pr-1">
            {content}
          </p>
          <ConfirmModal
            trigger={
              <IconButton
                icon={<TrashIcon />}
                className="opacity-0 group-hover:opacity-100 transition-all"
              />
            }
            title={t('agent_creation.content.delete_title')}
            description={t('agent_creation.content.delete_desc')}
            cancel={t('common.cancel')}
            confirm={t('common.delete')}
            onConfirm={onDelete}
            loading={deleteLoading}
          />
        </div>
      </div>
    </div>
  );
};

export type PodcastGeneratingItemProps = { createdAt: number | Date; content: string; className?: string; };
export const PodcastGeneratingItem: React.FC<PodcastGeneratingItemProps> = ({ createdAt, content, className = "" }) => {
  const { t } = useTranslation();
  const pct = progressPercent(createdAt);
  return (
    <div className={className}>
      <div className="flex items-center">
        <div className="relative w-[72px] h-[72px] rounded-[8px] overflow-hidden shrink-0">
          <div className="w-full h-full bg-[linear-gradient(135deg,_rgba(189,168,255,1)_0%,_rgba(188,226,255,1)_100%)]" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex flex-col items-center">
              <span className="text-white text-[14px] font-medium">{pct}%</span>
              <span className="text-white text-[10px] font-medium mt-1">
                {t('agent_creation.content.regenerating')}
              </span>
            </div>
          </div>
        </div>
        <div className="ml-3 flex-1 h-[72px] flex flex-col">
          <p className="text-[14px] text-[rgba(16,24,40,1)] line-clamp-2">{content}</p>
        </div>
      </div>
    </div>
  );
};

export type PodcastFailedItemProps = { content: string; onRegenerate?: () => void; onDelete?: () => void; deleteLoading?: boolean; className?: string; };
export const PodcastFailedItem: React.FC<PodcastFailedItemProps> = ({ content, onRegenerate, onDelete, deleteLoading = false, className = "" }) => {
  const { t } = useTranslation();
  return (
    <div className={className}>
      <div className="flex items-center">
        <button type="button" onClick={onRegenerate} className="relative w-[72px] h-[72px] rounded-[8px] overflow-hidden shrink-0">
          <div className="w-full h-full bg-[linear-gradient(135deg,_rgba(189,168,255,1)_0%,_rgba(188,226,255,1)_100%)]" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex flex-col items-center">
              <RefreshIcon className="w-4 h-4 text-white" />
              <span className="text-white text-[10px] font-medium mt-1">
                {t('agent_creation.content.regenerate')}
              </span>
            </div>
          </div>
        </button>
        <div className="h-[72px] group ml-3 flex flex-1 items-center justify-between">
          <p className="text-[14px] text-[#333333] line-clamp-2 pr-1">
            {content}
          </p>
          <ConfirmModal
            trigger={
              <IconButton
                icon={<TrashIcon />}
                className="opacity-0 group-hover:opacity-100 transition-all"
              />
            }
            title={t('agent_creation.content.delete_title')}
            description={t('agent_creation.content.delete_desc')}
            cancel={t('common.cancel')}
            confirm={t('common.delete')}
            onConfirm={onDelete}
            loading={deleteLoading}
          />
        </div>
      </div>
    </div>
  );
};

export type PodcastRecordItemProps = {
  coverUrl: string;
  content: string;
  audioUrl: string;
  duration?: number;
  className?: string;
  isActive: boolean;
  deleteLoading?: boolean;
  onDelete?: () => void;
  onTap?: () => void;
  onTogglePlay: (showControls: boolean) => void;
};
export const PodcastRecordItem: React.FC<PodcastRecordItemProps> = ({
  coverUrl,
  content,
  audioUrl,
  duration: initialDuration,
  onTap,
  className = "",
  isActive,
  deleteLoading = false,
  onDelete,
  onTogglePlay,
}) => {
  const { t } = useTranslation();
  const {
    playState,
    duration,
    currentTime,
    playbackRate,
    muted,
    play,
    stop,
    seekTo,
    volume,
    setVolume,
    setMuted,
    setPlaybackRate,
    startPlaybackRateDrag,
    endPlaybackRateDrag,
    toggleMuted,
  } = audioControllerHooks.useController();

  const capsuleDurationLabel = useMemo(() => {
    const total = (duration || initialDuration || 0);
    const fmt = (t: number) => {
      const m = Math.floor(t / 60);
      const s = Math.floor(t % 60).toString().padStart(2, "0");
      return `${m}:${s}`;
    };
    if (
      playState === AudioPlayState.IDLE ||
      playState === AudioPlayState.LOADING ||
      playState === AudioPlayState.ENDED ||
      currentTime <= 0
    ) {
      return fmt(total);
    }
    const remain = Math.max(0, total - currentTime);
    return fmt(remain);
  }, [playState, duration, currentTime, initialDuration]);

  const handleTogglePlay = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!audioUrl) {
      return;
    }
    if (playState === AudioPlayState.PLAYING) {
      onTogglePlay(false);
      stop();
    } else {
      onTogglePlay(true);
      await play(audioUrl);
    }
  };

  // 有效播放状态
  const isEffectivelyPlaying = useMemo(() => {
    return isActive &&
      playState !== AudioPlayState.IDLE &&
      playState !== AudioPlayState.ENDED;
  }, [isActive, playState]);

  return (
    <div onClick={onTap} className={className} role="button" tabIndex={0}>
      <div className="flex items-center group">
        <div className="relative size-20 rounded-[8px] overflow-hidden shrink-0">
          <Image src={coverUrl} alt="cover" className="w-full h-full object-cover" />
        </div>
        <div className="ml-3 flex-1">
          <div className="text-[14px] text-[rgba(16,24,40,1)] line-clamp-2 flex items-center">
            <p className="flex-1 line-clamp-2 pr-1 text-[#333333]">{content}</p>
            <ConfirmModal
              trigger={
                <IconButton
                  icon={<TrashIcon />}
                  className="opacity-0 group-hover:opacity-100 transition-all"
                />
              }
              title={t('agent_creation.content.delete_title')}
              description={t('agent_creation.content.delete_desc')}
              cancel={t('common.cancel')}
              confirm={t('common.delete')}
              onConfirm={onDelete}
              loading={deleteLoading}
            />
          </div>
          <button
            type="button"
            className={cn(
              'inline-flex items-center gap-2 px-3 h-6 rounded-full mt-2',
              isEffectivelyPlaying ? 'bg-[rgba(16,24,40,1)] text-white' : 'bg-[rgba(234,236,240,1)] text-[rgba(28,36,51,1)]'
            )}
            aria-label={isEffectivelyPlaying ? '暂停' : '播放'}
            onClick={handleTogglePlay}
          >
            {isEffectivelyPlaying ? (
              <StopIcon className="w-3 h-4 text-white" />
            ) : (
              <PlayIcon className="w-3 h-4" />
            )}
            <span className="text-xs font-medium">{capsuleDurationLabel}</span>
          </button>
        </div>
      </div>
      {/* 底部：播放控制条 */}
      {isEffectivelyPlaying && (
        <AudioControllerBar
          audioUrl={audioUrl}
          copyContent={content}
          showShare
          duration={duration}
          currentTime={currentTime}
          playState={playState}
          playbackRate={playbackRate}
          muted={muted}
          volume={volume}
          seekTo={seekTo}
          setPlaybackRate={setPlaybackRate}
          startPlaybackRateDrag={startPlaybackRateDrag}
          endPlaybackRateDrag={endPlaybackRateDrag}
          toggleMuted={toggleMuted}
          setVolume={setVolume}
          setMuted={setMuted}
        />
      )}
    </div>
  );
};

export type MomentsRecordItemProps = { images: string[]; content: string; onTap?: () => void; className?: string; onDelete?: () => void; deleteLoading?: boolean; };
export const MomentsRecordItem: React.FC<MomentsRecordItemProps> = ({ images, content, onTap, className = "", onDelete, deleteLoading = false }) => {
  const { t } = useTranslation();
  const cover = images && images.length > 0 ? images[0] : undefined;
  return (
    <div onClick={onTap} className={className} role="button" tabIndex={0}>
      <div className="flex items-center group">
        {<div className="relative size-20 rounded-[8px] overflow-hidden shrink-0 mr-3">
          <Image src={cover || momentsBg2} alt="moments" className="w-full h-full object-cover" />
        </div>}
        <div className="flex-1 flex items-center">
          <p className="text-[14px] text-[#333333] line-clamp-2 pr-1">{content}</p>
          <ConfirmModal
            trigger={
              <IconButton
                icon={<TrashIcon />}
                className="opacity-0 group-hover:opacity-100 transition-all"
              />
            }
            title={t('agent_creation.content.delete_title')}
            description={t('agent_creation.content.delete_desc')}
            cancel={t('common.cancel')}
            confirm={t('common.delete')}
            onConfirm={onDelete}
            loading={deleteLoading}
          />
        </div>
      </div>
    </div>
  );
};

export type MomentsGeneratingItemProps = { createdAt: number | Date; content: string; className?: string; };
export const MomentsGeneratingItem: React.FC<MomentsGeneratingItemProps> = ({ createdAt, content, className = "" }) => {
  const { t } = useTranslation();
  const pct = progressPercent(createdAt);
  return (
    <div className={className}>
      <div className="flex items-center">
        <div className="relative size-20 rounded-[8px] overflow-hidden shrink-0">
          <div className="w-full h-full bg-[linear-gradient(135deg,_rgba(189,168,255,1)_0%,_rgba(188,226,255,1)_100%)]" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex flex-col items-center">
              <span className="text-white text-[14px] font-medium">{pct}%</span>
              <span className="text-white text-[10px] font-medium mt-1">
                {t('agent_creation.content.regenerating')}
              </span>
            </div>
          </div>
        </div>
        <div className="ml-3 flex-1 h-[72px] flex flex-col"><p className="text-[14px] text-[#333333] line-clamp-2">{content}</p></div>
      </div>
    </div>
  );
};

export type MomentsFailedItemProps = { content: string; onRegenerate?: () => void; onDelete?: () => void; className?: string; deleteLoading?: boolean; };
export const MomentsFailedItem: React.FC<MomentsFailedItemProps> = ({ content, onRegenerate, onDelete, className = "", deleteLoading = false }) => {
  const { t } = useTranslation();
  return (
    <div className={className}>
      <div className="flex items-center">
        <button type="button" onClick={onRegenerate} className="relative w-[72px] h-[72px] rounded-[8px] overflow-hidden shrink-0">
          <div className="w-full h-full bg-[linear-gradient(135deg,_rgba(189,168,255,1)_0%,_rgba(188,226,255,1)_100%)]" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex flex-col items-center">
              <RefreshIcon className="w-4 h-4 text-white" />
              <span className="text-white text-[10px] font-medium mt-1">
                {t('agent_creation.content.regenerate')}
              </span>
            </div>
          </div>
        </button>
        <div className="ml-3 flex-1 h-20 flex flex-col">
          <p className="text-[14px] text-[#333333] line-clamp-2 pr-1">{content}</p>
          <ConfirmModal
            trigger={
              <div className="mt-auto flex justify-end">
                <button type="button" onClick={onDelete} className="flex items-center gap-1 text-[13px] text-[rgba(40,47,62,1)] h-7 px-2.5 py-1 rounded-md border border-[rgb(233,234,235)] bg-white hover:bg-[rgba(249,250,251,1)] transition-all">
                  <TrashIcon className="w-4 h-4 text-[rgba(40,47,62,1)]" />
                  <span>{t('agent_creation.content.delete')}</span>
                </button>
              </div>
            }
            title={t('agent_creation.content.delete_title')}
            description={t('agent_creation.content.delete_desc')}
            cancel={t('common.cancel')}
            confirm={t('common.delete')}
            onConfirm={onDelete}
            loading={deleteLoading}
          />
        </div>
      </div>
    </div>
  );
};

export type PostRecordItemProps = { images: string[]; title: string; onTap?: () => void; className?: string; deleteLoading?: boolean; onDelete?: () => void; };
export const PostRecordItem: React.FC<PostRecordItemProps> = ({ images, title, onTap, className = "", deleteLoading = false, onDelete }) => {
  const { t } = useTranslation();
  const imgs = Array.isArray(images) ? images.slice(0, 3) : [];
  return (
    <div onClick={onTap} className={className} role="button" tabIndex={0}>
      <div className="flex items-center group">
        <div className="relative size-20 shrink-0">
          <Image
            className={`absolute size-20 object-cover rounded-md origin-center border border-[#eaecf0cd]`}
            src={imgs[0]}
            alt={`post-1`}
          />
        </div>
        <div className="ml-3 flex-1 flex items-center">
          <p className="text-[14px] text-[#333333] line-clamp-2 pr-1">{title}</p>
          <ConfirmModal
            trigger={
              <IconButton
                icon={<TrashIcon />}
                className="opacity-0 group-hover:opacity-100 transition-all"
              />
            }
            title={t('agent_creation.content.delete_title')}
            description={t('agent_creation.content.delete_desc')}
            cancel={t('common.cancel')}
            confirm={t('common.delete')}
            onConfirm={onDelete}
            loading={deleteLoading}
          />
        </div>
      </div>
    </div>
  );
};

export type PostGeneratingItemProps = { createdAt: number | Date; content: string; className?: string; };
export const PostGeneratingItem: React.FC<PostGeneratingItemProps> = ({ createdAt, content, className = "" }) => {
  const { t } = useTranslation();
  const pct = progressPercent(createdAt);
  return (
    <div className={className}>
      <div className="flex items-center">
        <div className="relative size-20 rounded-[8px] overflow-hidden shrink-0">
          <div className="w-full h-full bg-[linear-gradient(135deg,_rgba(189,168,255,1)_0%,_rgba(188,226,255,1)_100%)]" />
          <div className="absolute inset-0 flex items-center justify-center"><div className="flex flex-col items-center">
            <span className="text-white text-[14px] font-medium">{pct}%</span>
            <span className="text-white text-[10px] font-medium mt-1">
              {t('agent_creation.content.regenerating')}
            </span>
          </div>
          </div>
        </div>
        <div className="ml-3 flex-1 h-[72px] flex flex-col"><p className="text-[14px] text-[#333333] line-clamp-2">{content}</p></div>
      </div>
    </div>
  );
};

export type PostFailedItemProps = { content: string; onRegenerate?: () => void; onDelete?: () => void; className?: string; deleteLoading?: boolean; };
export const PostFailedItem: React.FC<PostFailedItemProps> = ({ content, onRegenerate, onDelete, className = "", deleteLoading = false }) => {
  const { t } = useTranslation();
  return (
    <div className={className}>
      <div className="flex items-center">
        <button type="button" onClick={onRegenerate} className="relative size-20 rounded-[8px] overflow-hidden shrink-0">
          <div className="w-full h-full bg-[linear-gradient(135deg,_rgba(189,168,255,1)_0%,_rgba(188,226,255,1)_100%)]" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex flex-col items-center">
              <RefreshIcon className="w-4 h-4 text-white" />
              <span className="text-white text-[10px] font-medium mt-1">
                {t('agent_creation.content.regenerate')}
              </span>
            </div>
          </div>
        </button>
        <div className="h-20 group ml-3 flex-1 flex items-center justify-between">
          <p className="text-[14px] text-[#333333] line-clamp-2">{content}</p>
          <ConfirmModal
            trigger={
              <IconButton
                icon={<TrashIcon />}
                className="opacity-0 group-hover:opacity-100 transition-all"
              />
            }
            title={t('agent_creation.content.delete_title')}
            description={t('agent_creation.content.delete_desc')}
            cancel={t('common.cancel')}
            confirm={t('common.delete')}
            onConfirm={onDelete}
            loading={deleteLoading}
          />
        </div>
      </div>
    </div>
  );
};

export type ArticleRecordItemProps = { coverUrl: string; title: string; onTap?: () => void; className?: string; deleteLoading?: boolean; onDelete?: () => void; };
export const ArticleRecordItem: React.FC<ArticleRecordItemProps> = ({ coverUrl, title, onTap, className = "", deleteLoading = false, onDelete }) => {
  const { t } = useTranslation();
  return (
    <div onClick={onTap} className={className} role="button" tabIndex={0}>
      <div className="flex items-center group">
        <div className="relative size-20 rounded-[8px] overflow-hidden shrink-0"><Image src={coverUrl} alt="article" className="w-full h-full object-cover" /></div>
        <div className="ml-3 flex-1 flex items-center">
          <p className="text-[14px] text-[#333333] line-clamp-2 pr-1">{title}</p>
          <ConfirmModal
            trigger={
              <IconButton
                icon={<TrashIcon />}
                className="opacity-0 group-hover:opacity-100 transition-all"
              />
            }
            title={t('agent_creation.content.delete_title')}
            description={t('agent_creation.content.delete_desc')}
            cancel={t('common.cancel')}
            confirm={t('common.delete')}
            onConfirm={onDelete}
            loading={deleteLoading}
          />
        </div>
      </div>
    </div>
  );
};

export type ArticleGeneratingItemProps = { createdAt: number | Date; title: string; className?: string; };
export const ArticleGeneratingItem: React.FC<ArticleGeneratingItemProps> = ({ createdAt, title, className = "" }) => {
  const { t } = useTranslation();
  const pct = progressPercent(createdAt);
  return (
    <div className={className}>
      <div className="flex items-center">
        <div className="relative size-20 rounded-[8px] overflow-hidden shrink-0">
          <div className="w-full h-full bg-[linear-gradient(135deg,_rgba(189,168,255,1)_0%,_rgba(188,226,255,1)_100%)]" />
        </div>
        <div className="absolute size-20 flex items-center justify-center">
          <div className="flex flex-col items-center">
            <span className="text-white text-[14px] font-medium">{pct}%</span>
            <span className="text-white text-[10px] font-medium mt-1">
              {t('agent_creation.content.regenerating')}
            </span>
          </div>
        </div>
        <div className="ml-3 flex-1 h-20 flex flex-col"><p className="text-[14px] text-[#333333] line-clamp-2">{title}</p></div>
      </div>
    </div>
  );
};

export type ArticleFailedItemProps = { title: string; onRegenerate?: () => void; onDelete?: () => void; className?: string; deleteLoading?: boolean; };
export const ArticleFailedItem: React.FC<ArticleFailedItemProps> = ({ title, onRegenerate, onDelete, className = "", deleteLoading = false }) => {
  const { t } = useTranslation();
  return (
    <div className={className}>
      <div className="flex items-center">
        <button type="button" onClick={onRegenerate} className="relative size-20 rounded-[8px] overflow-hidden shrink-0">
          <div className="w-full h-full bg-[linear-gradient(135deg,_rgba(189,168,255,1)_0%,_rgba(188,226,255,1)_100%)]" />
          <div className="absolute inset-0 size-20 flex items-center justify-center">
            <div className="flex flex-col items-center">
              <RefreshIcon className="w-4 h-4 text-white" />
              <span className="text-white text-[10px] font-medium mt-1">{t('agent_creation.content.regenerate')}
              </span>
            </div>
          </div>
        </button>
        <div className="h-20 group ml-3 flex-1 flex items-center justify-between">
          <p className="text-[14px] text-[#333333] line-clamp-2">{title}</p>
          <ConfirmModal
            trigger={
              <IconButton
                icon={<TrashIcon />}
                className="opacity-0 group-hover:opacity-100 transition-all"
              />
            }
            title={t('agent_creation.content.delete_title')}
            description={t('agent_creation.content.delete_desc')}
            cancel={t('common.cancel')}
            confirm={t('common.delete')}
            onConfirm={onDelete}
            loading={deleteLoading}
          />
        </div>
      </div>
    </div>
  );
};

export type AiRecordItemProps = {
  id: number;
  url: string;
  type: 'image' | 'video';
  className?: string;
  openSelected?: boolean,
  selectedIds?: number[],
  duration: number,
  onClick?: () => void,
  onSelected?: (id: number, checked: boolean) => void,
};
export const AiRecordItem: React.FC<AiRecordItemProps> = ({
  id,
  url,
  type,
  duration,
  className,
  openSelected,
  selectedIds = [],
  onSelected,
  onClick,
}) => {
  const { t } = useTranslation();

  // 检查是否选中
  const checkSelected = (id: number) => selectedIds?.includes(id);

  const handleSelect = (value: boolean, id: number) => {
    onSelected?.(id, value);
  }

  return (
    <div
      key={id}
      className={cn("group relative hover:cursor-pointer", className)}
      onClick={(e) => {
        e.stopPropagation();
        if (checkSelected(id) || selectedIds?.length || openSelected) {
          handleSelect(!checkSelected(id), id)
        } else {
          onClick?.();
        }
      }}
    >
      {type === 'image' && (
        <FallbackImage
          primarySrc={buildImageOssResize(url, { w: 102 * 3, h: 100 * 3 })}
          fallbackSrc={url}
          className="w-full h-auto rounded-[8px]"
        />
        // <Image src={url} className="w-full h-auto rounded-[8px]" />
      )}
      {type === 'video' && (
        <>
          <Video
            src={url}
            poster={buildVideoOssFrame(url, { w: 200, h: 0 })}
            className="w-full h-auto rounded-[8px]"
          // preload="none"
          />
          <span className="absolute bottom-1 left-1 text-white text-[11px] font-medium py-[3px] px-[5px] bg-[rgba(0,0,0,0.2)] border border-[rgba(0,0,0,0.3)] rounded-[6px]">{formatAudioTime(duration)}</span>
        </>
      )}

      <RadioButton
        checked={checkSelected(id)}
        onChange={(value) => handleSelect(value, id)}
        className={cn(
          "absolute size-5 top-2 left-2 rounded-[4px] border-[1.67px] border-white bg-[rgba(0,0,0,0.34)] ",
          "opacity-0 group-hover:opacity-100 [box-shadow:0px_1px_1px_rgba(0,_0,_0,_0.25)]",
          openSelected && "opacity-100",
          checkSelected(id) && "opacity-100 bg-[rgba(0,0,0,0.67)]",
        )}
      />
    </div>
  );
}

interface AiRecordListProps {
  records: AiMakeMessageRecordData[];
  selectedIds?: number[];
  onPreview?: (id: number) => void;
  onSelected?: (id: number, checked: boolean) => void;
}

export const AiRecordList: React.FC<AiRecordListProps> = ({
  records,
  selectedIds = [],
  onSelected,
  onPreview,
}) => {

  // 计算每个项目的权重（高度估计）
  const getItemWeight = (item: AiMakeMessageRecordData): number => {
    // 图片类型使用OSS缩放尺寸的宽高比 (306x300 ≈ 1.02)
    // 视频类型假设为正方形 (1:1)
    const aspectRatio = item.type === 1 ? 306 / 300 : 1.0;
    // 权重与高度成正比，高度 = 宽度 / 宽高比，宽度固定，所以权重 = 1 / 宽高比
    return 1 / aspectRatio;
  };

  // 瀑布流分配算法：将项目分配到列，使各列总权重平衡
  const getColumns = (data: AiMakeMessageRecordData[], columnCount = 4) => {
    const columns = Array.from({ length: columnCount }, () => [] as AiMakeMessageRecordData[]);
    const columnHeights = Array.from({ length: columnCount }, () => 0);

    data.forEach((item) => {
      // 找到当前高度最小的列
      let minIndex = 0;
      let minHeight = columnHeights[0];
      for (let i = 1; i < columnCount; i++) {
        if (columnHeights[i] < minHeight) {
          minHeight = columnHeights[i];
          minIndex = i;
        }
      }
      // 将项目添加到该列
      columns[minIndex].push(item);
      // 更新该列的总高度（加上项目权重和间隙权重）
      columnHeights[minIndex] += getItemWeight(item);
    });

    return columns;
  };

  const columns = useMemo(() => getColumns(records, 4), [records]);

  return records.length > 0 && (
    // 横向排列
    <div className="flex gap-2">
      {columns.map((col, colIdx) => (
        <div key={colIdx} className="flex-1 flex flex-col gap-2">
          {col.map((item, imgIdx) => (
            <AiRecordItem
              id={item.id}
              url={item.s3Url || item.url}
              type={item.type === 1 ? 'image' : 'video'}
              openSelected={selectedIds.length > 0}
              selectedIds={selectedIds}
              onSelected={onSelected}
              duration={item.videoDuration}
              onClick={() => onPreview?.(item.id)}
            />
          ))}
        </div>
      ))}
    </div>
    // 竖向排列
    // <div className="columns-4 gap-2 space-y-2">
    //   {records.map((item, index) => (
    //     <AiRecordItem
    //       id={index + ''}
    //       url={item}
    //     />
    //   ))}
    // </div>
  )
}